import { createRoute } from '@tanstack/react-router';
import { useState, useEffect, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Ban, Eye, Users, Package, DollarSign, Repeat, Tag, Handshake, CalendarPlus, FileArchive, HardDrive, AlertTriangle, AlertOctagon, Sun, Copy, Check, ShieldCheck, ShieldOff, Activity, BarChart3, TrendingUp, Crown, UserCheck, Bug, Lightbulb, MessageCircle, HelpCircle, Send, ChevronDown, MessageSquareText } from 'lucide-react';
import { Shell } from '../components/layout/Shell';
import { FeatureDisabledBanner } from '../components/FeatureDisabledBanner';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../hooks/useAuth';
import { useAppMode } from '../context/AppModeContext';
import { useTranslation } from '../i18n';
import { useDebounce } from '../hooks/useDebounce';
import { apiClient } from '../api/client';
import { useInventoryList } from '../hooks/useInventory';
import { ItemTable } from '../components/inventory/ItemTable';
import {
  useUsers,
  useInviteUser,
  useUpdateUserRole,
  useUpdateUserTier,
  useResetUserPassword,
  useBanUser,
  useAdminStats,
  useAdminAnalytics,
  useUserInventory,
  useAdminOffers,
  useForceWithdrawOffer,
  useAdminLicenses,
  useLicenseStats,
  useIssueLicense,
  useRevokeLicense,
  useDeleteLicense,
} from '../hooks/useAdmin';
import {
  useAdminFeedbackList,
  useAdminFeedbackStats,
  useAdminUpdateFeedbackStatus,
  useAdminReplyFeedback,
} from '../hooks/useFeedback';
import { AcquisitionLineChart } from '../components/charts/AcquisitionLineChart';
import { PlatformPieChart } from '../components/charts/PlatformPieChart';
import { EngagementMultiLineChart } from '../components/charts/EngagementMultiLineChart';
import { ViewsAreaChart } from '../components/charts/ViewsAreaChart';
import { ReferrerBarChart } from '../components/charts/ReferrerBarChart';
import { CATEGORIES, PLATFORMS } from '@siliconvault/shared';
import { Route as rootRoute } from './__root';
import styles from './admin.module.css';

type AdminTab = 'overview' | 'analytics' | 'users' | 'inventory' | 'offers' | 'licenses' | 'feedback';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: AdminPage,
});

function AdminPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { mode } = useAppMode();
  const [activeTab, setActiveTab] = useState<AdminTab>('overview');

  if (mode === 'desktop') {
    return (
      <Shell>
        <FeatureDisabledBanner feature="admin" />
      </Shell>
    );
  }

  if (user?.role !== 'admin') {
    return (
      <Shell>
        <div className={styles.page}>
          <h1 className={styles.title}>{t('admin.accessDenied')}</h1>
          <p className={styles.message}>{t('admin.accessDeniedMessage')}</p>
        </div>
      </Shell>
    );
  }

  const tabs: { key: AdminTab; label: string }[] = [
    { key: 'overview', label: t('admin.tabOverview') },
    { key: 'analytics', label: 'Analytics' },
    { key: 'users', label: t('admin.tabUsers') },
    { key: 'inventory', label: t('admin.tabInventory') },
    { key: 'offers', label: t('admin.tabOffers') },
    { key: 'licenses', label: 'Licenses' },
    { key: 'feedback', label: t('admin.feedbackTab') },
  ];

  return (
    <Shell>
      <div className={styles.page}>
        <h1 className={styles.title}>{t('admin.title')}</h1>

        <div className={styles.tabs}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && <StatsPanel />}
        {activeTab === 'analytics' && <AnalyticsPanel />}
        {activeTab === 'users' && <UsersPanel />}
        {activeTab === 'inventory' && <InventoryPanel />}
        {activeTab === 'offers' && <OffersPanel />}
        {activeTab === 'licenses' && <LicensesPanel />}
        {activeTab === 'feedback' && <FeedbackPanel />}
      </div>
    </Shell>
  );
}

/* ─── Stats (Overview Tab) ──────────────────────────────────────────────── */

function StatsPanel() {
  const { t } = useTranslation();
  const { data, isLoading } = useAdminStats();

  if (isLoading) {
    return <div className={styles.loading}>{t('admin.loadingStats')}</div>;
  }

  const stats = data?.data;
  if (!stats) return null;

  function formatBytes(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }

  return (
    <div className={styles.overviewLayout}>
      {/* Hero row */}
      <div className={styles.heroGrid}>
        <div className={styles.heroCard}>
          <div className={styles.heroIcon}><Users size={24} strokeWidth={1.5} /></div>
          <div className={styles.heroContent}>
            <span className={styles.heroValue}>{stats.totalUsers}</span>
            <span className={styles.heroLabel}>{t('admin.statTotalUsers')}</span>
          </div>
        </div>
        <div className={styles.heroCard}>
          <div className={styles.heroIcon}><Package size={24} strokeWidth={1.5} /></div>
          <div className={styles.heroContent}>
            <span className={styles.heroValue}>{stats.totalItems}</span>
            <span className={styles.heroLabel}>{t('admin.statTotalItems')}</span>
          </div>
        </div>
        <div className={styles.heroCard}>
          <div className={styles.heroIcon}><DollarSign size={24} strokeWidth={1.5} /></div>
          <div className={styles.heroContent}>
            <span className={styles.heroValue}>${Number(stats.totalValue).toLocaleString()}</span>
            <span className={styles.heroLabel}>{t('admin.statTotalValue')}</span>
          </div>
        </div>
      </div>

      {/* Community row */}
      <div className={styles.sectionLabel}>{t('admin.sectionCommunity')}</div>
      <div className={styles.metricsGrid}>
        <div className={styles.metricCard}>
          <Repeat size={16} strokeWidth={1.5} className={styles.metricIcon} />
          <span className={styles.metricValue}>{stats.forTrade}</span>
          <span className={styles.metricLabel}>{t('admin.statForTrade')}</span>
        </div>
        <div className={styles.metricCard}>
          <Tag size={16} strokeWidth={1.5} className={styles.metricIcon} />
          <span className={styles.metricValue}>{stats.forSale}</span>
          <span className={styles.metricLabel}>{t('admin.statForSale')}</span>
        </div>
        <div className={styles.metricCard}>
          <Handshake size={16} strokeWidth={1.5} className={styles.metricIcon} />
          <span className={styles.metricValue}>{stats.pendingOffers}</span>
          <span className={styles.metricLabel}>{t('admin.statActiveTrades')}</span>
        </div>
        <div className={styles.metricCard}>
          <CalendarPlus size={16} strokeWidth={1.5} className={styles.metricIcon} />
          <span className={styles.metricValue}>{stats.newThisMonth}</span>
          <span className={styles.metricLabel}>{t('admin.statNewThisMonth')}</span>
        </div>
      </div>

      {/* System row */}
      <div className={styles.sectionLabel}>{t('admin.sectionSystem')}</div>
      <div className={styles.metricsGrid}>
        <div className={styles.metricCard}>
          <FileArchive size={16} strokeWidth={1.5} className={styles.metricIcon} />
          <span className={styles.metricValue}>{stats.libraryFiles}</span>
          <span className={styles.metricLabel}>{t('admin.statLibraryFiles')}</span>
        </div>
        <div className={styles.metricCard}>
          <HardDrive size={16} strokeWidth={1.5} className={styles.metricIcon} />
          <span className={styles.metricValue}>{formatBytes(Number(stats.librarySize))}</span>
          <span className={styles.metricLabel}>{t('admin.statLibrarySize')}</span>
        </div>
        <div className={styles.metricCard} data-severity={stats.criticalAlerts > 0 ? 'critical' : undefined}>
          <AlertOctagon size={16} strokeWidth={1.5} className={styles.metricIcon} />
          <span className={styles.metricValue}>{stats.criticalAlerts}</span>
          <span className={styles.metricLabel}>{t('admin.statCriticalAlerts')}</span>
        </div>
        <div className={styles.metricCard} data-severity={stats.unresolvedAlerts > 0 ? 'high' : undefined}>
          <AlertTriangle size={16} strokeWidth={1.5} className={styles.metricIcon} />
          <span className={styles.metricValue}>{stats.unresolvedAlerts}</span>
          <span className={styles.metricLabel}>{t('admin.statUnresolvedAlerts')}</span>
        </div>
        <div className={styles.metricCard}>
          <Sun size={16} strokeWidth={1.5} className={styles.metricIcon} />
          <span className={styles.metricValue}>{stats.activeRetrobright}</span>
          <span className={styles.metricLabel}>{t('admin.statActiveRetrobright')}</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Analytics Tab ─────────────────────────────────────────────────────── */

const ANALYTICS_PERIOD_KEY = 'sv-admin-analytics-period';

function AnalyticsPanel() {
  const [months, setMonths] = useState(() => {
    const saved = localStorage.getItem(ANALYTICS_PERIOD_KEY);
    return saved ? Number(saved) : 12;
  });

  function handlePeriodChange(value: number) {
    setMonths(value);
    localStorage.setItem(ANALYTICS_PERIOD_KEY, String(value));
  }
  const { data, isLoading } = useAdminAnalytics(months);
  const analytics = data?.data;

  if (isLoading) {
    return <div className={styles.loading}>Loading analytics…</div>;
  }

  if (!analytics) return null;

  const { growth, engagement, composition, business } = analytics;

  return (
    <div className={styles.overviewLayout}>
      {/* Period selector */}
      <div className={styles.analyticsControls}>
        <label htmlFor="analytics-period">Period</label>
        <select
          id="analytics-period"
          className={styles.roleSelect}
          value={months}
          onChange={(e) => handlePeriodChange(Number(e.target.value))}
        >
          <option value={1}>Last 24 hours</option>
          <option value={2}>Last 48 hours</option>
          <option value={3}>Last week</option>
          <option value={6}>Last 6 months</option>
          <option value={12}>Last 12 months</option>
          <option value={24}>Last 24 months</option>
        </select>
      </div>

      {/* ── Growth ─────────────────────────────────────────────────── */}
      <div className={styles.sectionLabel}>Growth</div>

      <div className={styles.heroGrid}>
        <div className={styles.heroCard}>
          <div className={styles.heroIcon}><UserCheck size={24} strokeWidth={1.5} /></div>
          <div className={styles.heroContent}>
            <span className={styles.heroValue}>{growth.activeUsers.dau}</span>
            <span className={styles.heroLabel}>Active last 24h</span>
          </div>
        </div>
        <div className={styles.heroCard}>
          <div className={styles.heroIcon}><TrendingUp size={24} strokeWidth={1.5} /></div>
          <div className={styles.heroContent}>
            <span className={styles.heroValue}>{growth.activeUsers.wau}</span>
            <span className={styles.heroLabel}>Active last 7 days</span>
          </div>
        </div>
        <div className={styles.heroCard}>
          <div className={styles.heroIcon}><Users size={24} strokeWidth={1.5} /></div>
          <div className={styles.heroContent}>
            <span className={styles.heroValue}>{growth.activeUsers.mau}</span>
            <span className={styles.heroLabel}>Active last 30 days</span>
          </div>
        </div>
      </div>

      <div className={styles.analyticsChartGrid}>
        <AcquisitionLineChart data={growth.signupTrend} title="Signup Trend" />
        <PlatformPieChart
          data={[
            { platform: 'Active', count: growth.userStatus.active },
            { platform: 'Dormant', count: growth.userStatus.dormant },
          ]}
          title="User Status"
        />
      </div>

      {/* ── Engagement ─────────────────────────────────────────────── */}
      <div className={styles.sectionLabel}>Engagement</div>

      <div className={styles.analyticsChartGrid}>
        <AcquisitionLineChart data={engagement.itemsPerMonth} title="Items Added / Month" />
        <AcquisitionLineChart data={engagement.aiAnalysesPerMonth} title="AI Analyses / Month" />
        <EngagementMultiLineChart
          offers={engagement.offersPerMonth}
          favorites={engagement.favoritesPerMonth}
          messages={engagement.messagesPerMonth}
          title="Offers · Favorites · Messages"
        />
        <ViewsAreaChart data={engagement.viewsPerMonth} title="Profile & Catalog Views" />
      </div>

      {/* ── Composition ────────────────────────────────────────────── */}
      <div className={styles.sectionLabel}>Composition</div>

      <div className={styles.analyticsChartGrid}>
        <PlatformPieChart
          data={composition.vaultTypes.map((v: { name: string; count: number }) => ({ platform: v.name, count: v.count }))}
          title="Vault Types"
        />
        <PlatformPieChart
          data={composition.aiProviders.map((p: { name: string; count: number }) => ({ platform: p.name, count: p.count }))}
          title="AI Providers"
        />
        <PlatformPieChart
          data={composition.tierDistribution.map((d: { name: string; count: number }) => ({ platform: d.name, count: d.count }))}
          title="Tier Distribution"
        />
        <ReferrerBarChart data={composition.topReferrers} title="Top Referrers" />
      </div>

      {/* ── Business ───────────────────────────────────────────────── */}
      <div className={styles.sectionLabel}>Business</div>

      <div className={styles.metricsGrid}>
        <div className={styles.metricCard}>
          <Crown size={16} strokeWidth={1.5} className={styles.metricIcon} />
          <span className={styles.metricValue}>{business.proConversionRate}%</span>
          <span className={styles.metricLabel}>Pro Conversion</span>
        </div>
        <div className={styles.metricCard}>
          <Package size={16} strokeWidth={1.5} className={styles.metricIcon} />
          <span className={styles.metricValue}>{business.avgItemsPerUser}</span>
          <span className={styles.metricLabel}>Avg Items / User</span>
        </div>
        <div className={styles.metricCard}>
          <DollarSign size={16} strokeWidth={1.5} className={styles.metricIcon} />
          <span className={styles.metricValue}>${Math.round(business.avgCollectionValue).toLocaleString()}</span>
          <span className={styles.metricLabel}>Avg Collection Value</span>
        </div>
        <div className={styles.metricCard}>
          <BarChart3 size={16} strokeWidth={1.5} className={styles.metricIcon} />
          <span className={styles.metricValue}>{business.totalProUsers}</span>
          <span className={styles.metricLabel}>Pro Users</span>
        </div>
        <div className={styles.metricCard}>
          <Users size={16} strokeWidth={1.5} className={styles.metricIcon} />
          <span className={styles.metricValue}>{business.totalFreeUsers}</span>
          <span className={styles.metricLabel}>Free Users</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Users Tab ─────────────────────────────────────────────────────────── */

function UsersPanel() {
  const { user: currentUser } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();

  // Search, filter, pagination, sorting state
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const debouncedSearch = useDebounce(search, 300);

  const query: Record<string, string | number> = { page, limit: 20, sortBy, sortOrder };
  if (debouncedSearch) query.search = debouncedSearch;
  if (roleFilter) query.role = roleFilter;

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  }

  const { data, isLoading } = useUsers(query);
  const inviteMutation = useInviteUser();
  const updateRoleMutation = useUpdateUserRole();
  const updateTierMutation = useUpdateUserTier();
  const resetPasswordMutation = useResetUserPassword();
  const banMutation = useBanUser();

  const [showInvite, setShowInvite] = useState(false);
  const [banTarget, setBanTarget] = useState<{ id: string; name: string } | null>(null);
  const [resetTarget, setResetTarget] = useState<{ id: string; name: string } | null>(null);
  const [inventoryTarget, setInventoryTarget] = useState<{ id: string; name: string } | null>(null);

  // Invite form state
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'admin' | 'user'>('user');

  // Reset password form state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Reset page when filters change
  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }
  function handleRoleChange(value: string) {
    setRoleFilter(value);
    setPage(1);
  }

  async function handleInvite(e: FormEvent) {
    e.preventDefault();
    try {
      await inviteMutation.mutateAsync({ email, password, displayName, role });
      toast(t('admin.userAddedSuccess'), 'success');
      setShowInvite(false);
      setEmail(''); setDisplayName(''); setPassword(''); setRole('user');
    } catch (err: unknown) {
      toast(t('admin.failedToAddUser'), 'error');
    }
  }

  async function handleRoleUpdate(userId: string, newRole: 'admin' | 'user') {
    try {
      await updateRoleMutation.mutateAsync({ userId, role: newRole });
      toast(t('admin.roleUpdated'), 'success');
    } catch (err: unknown) {
      toast(t('admin.failedToUpdateRole'), 'error');
    }
  }

  async function handleTierUpdate(userId: string, tier: 'free' | 'pro') {
    try {
      await updateTierMutation.mutateAsync({ userId, tier });
      toast(`AI tier updated to ${tier}`, 'success');
    } catch {
      toast('Failed to update AI tier', 'error');
    }
  }

  async function handleResetPassword() {
    if (!resetTarget) return;
    setPasswordError('');

    if (newPassword.length < 8) {
      setPasswordError(t('admin.passwordMinLength'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(t('admin.passwordsDoNotMatch'));
      return;
    }

    try {
      await resetPasswordMutation.mutateAsync({ userId: resetTarget.id, password: newPassword });
      toast(`${t('admin.passwordResetFor')} ${resetTarget.name}`, 'success');
      setResetTarget(null);
      setNewPassword(''); setConfirmPassword(''); setPasswordError('');
    } catch (err: unknown) {
      toast(t('admin.failedToResetPassword'), 'error');
    }
  }

  async function handleBan() {
    if (!banTarget) return;
    try {
      await banMutation.mutateAsync(banTarget.id);
      toast(`${banTarget.name} ${t('admin.bannedSuccess')}`, 'success');
      setBanTarget(null);
    } catch (err: unknown) {
      toast(t('admin.failedToBan'), 'error');
    }
  }

  if (isLoading) {
    return <div className={styles.loading}>{t('admin.loadingUsers')}</div>;
  }

  const users = data?.data ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionCount}>{data?.total ?? 0} {(data?.total ?? 0) !== 1 ? t('common.users') : t('common.user')}</span>
        <Button size="small" onClick={() => setShowInvite(true)}>
          {t('admin.addUser')}
        </Button>
      </div>

      {/* Search & Filter Bar */}
      <div className={styles.filterBar}>
        <input
          className={styles.searchInput}
          type="text"
          placeholder={t('admin.searchUsersPlaceholder')}
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        <select
          className={styles.roleSelect}
          value={roleFilter}
          onChange={(e) => handleRoleChange(e.target.value)}
        >
          <option value="">{t('admin.allRoles')}</option>
          <option value="user">{t('admin.user')}</option>
          <option value="admin">{t('admin.adminRole')}</option>
        </select>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              {([
                { key: 'displayName', label: t('admin.name') },
                { key: 'email', label: t('admin.email') },
                { key: 'role', label: t('admin.role') },
                { key: 'aiTier', label: 'AI Tier' },
                { key: 'createdAt', label: t('admin.joined') },
                { key: 'lastLoginAt', label: t('admin.lastLogin') },
                { key: 'itemCount', label: t('admin.items') },
                { key: 'totalValue', label: t('admin.totalValue') },
              ] as const).map((col) => (
                <th
                  key={col.key}
                  className={styles.sortableHeader}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                  {sortBy === col.key && (
                    <span className={styles.sortIndicator}>
                      {sortOrder === 'asc' ? '\u25B2' : '\u25BC'}
                    </span>
                  )}
                </th>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isSelf = u.id === currentUser?.userId;
              return (
                <tr key={u.id}>
                  <td>
                    <button
                      className={styles.userNameButton}
                      onClick={() => setInventoryTarget({ id: u.id, name: u.displayName })}
                      title={t('admin.viewInventory')}
                    >
                      {u.displayName}
                    </button>
                    {isSelf && <span className={styles.youBadge}>({t('admin.you')})</span>}
                  </td>
                  <td className={styles.emailCell}>{u.email}</td>
                  <td>
                    {isSelf ? (
                      <span className={styles.roleBadge} data-role={u.role}>{u.role}</span>
                    ) : (
                      <select
                        className={styles.roleSelect}
                        value={u.role}
                        onChange={(e) => handleRoleUpdate(u.id, e.target.value as 'admin' | 'user')}
                        disabled={updateRoleMutation.isPending}
                      >
                        <option value="user">{t('admin.user')}</option>
                        <option value="admin">{t('admin.adminRole')}</option>
                      </select>
                    )}
                  </td>
                  <td>
                    <select
                      className={styles.roleSelect}
                      value={u.aiTier}
                      onChange={(e) => handleTierUpdate(u.id, e.target.value as 'free' | 'pro')}
                      disabled={updateTierMutation.isPending}
                    >
                      <option value="free">Free</option>
                      <option value="pro">Pro</option>
                    </select>
                  </td>
                  <td className={styles.dateCell}>
                    {new Date(u.createdAt).toLocaleDateString()}
                  </td>
                  <td className={styles.dateCell}>
                    {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : t('admin.never')}
                  </td>
                  <td className={styles.countCell}>{u.itemCount}</td>
                  <td className={styles.valueCell}>${Number(u.totalValue).toLocaleString()}</td>
                  <td>
                    {!isSelf && (
                      <div className={styles.rowActions}>
                        <Button
                          size="small"
                          variant="ghost"
                          title={t('admin.viewInventory')}
                          onClick={() => setInventoryTarget({ id: u.id, name: u.displayName })}
                        >
                          <Eye size={13} strokeWidth={2} />
                        </Button>
                        <Button
                          size="small"
                          variant="ghost"
                          title={t('admin.resetPasswordTitle')}
                          onClick={() => {
                            setResetTarget({ id: u.id, name: u.displayName });
                            setNewPassword(''); setConfirmPassword(''); setPasswordError('');
                          }}
                        >
                          <KeyRound size={13} strokeWidth={2} />
                          {t('admin.resetPw')}
                        </Button>
                        <Button
                          size="small"
                          variant="danger"
                          title={t('admin.banUserTitle')}
                          onClick={() => setBanTarget({ id: u.id, name: u.displayName })}
                        >
                          <Ban size={13} strokeWidth={2} />
                          {t('admin.ban')}
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <Button size="small" variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            {t('admin.prevPage')}
          </Button>
          <span className={styles.pageInfo}>
            {t('admin.pageOf').replace('{page}', String(page)).replace('{totalPages}', String(totalPages))}
          </span>
          <Button size="small" variant="secondary" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
            {t('admin.nextPage')}
          </Button>
        </div>
      )}

      {/* Add User modal */}
      <Modal
        isOpen={showInvite}
        onClose={() => setShowInvite(false)}
        title={t('admin.addUserTitle')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowInvite(false)}>{t('admin.cancel')}</Button>
            <Button onClick={handleInvite} disabled={inviteMutation.isPending}>
              {inviteMutation.isPending ? t('admin.adding') : t('admin.addUser')}
            </Button>
          </>
        }
      >
        <form className={styles.inviteForm} onSubmit={handleInvite}>
          <Input label={t('admin.displayName')} value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="John Doe" required />
          <Input label={t('admin.email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" required />
          <Input label={t('admin.password')} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('login.placeholderPassword')} required minLength={8} />
          <Select label={t('admin.role')} value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'user')}>
            <option value="user">{t('admin.user')}</option>
            <option value="admin">{t('admin.adminRole')}</option>
          </Select>
        </form>
      </Modal>

      {/* Reset Password modal */}
      <Modal
        isOpen={!!resetTarget}
        onClose={() => setResetTarget(null)}
        title={t('admin.resetPasswordTitle')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setResetTarget(null)}>{t('admin.cancel')}</Button>
            <Button onClick={handleResetPassword} disabled={resetPasswordMutation.isPending}>
              {resetPasswordMutation.isPending ? t('admin.resetting') : t('admin.resetPassword')}
            </Button>
          </>
        }
      >
        <div className={styles.resetForm}>
          <p className={styles.resetDesc}>
            {t('admin.resetPasswordDesc')} <strong>{resetTarget?.name}</strong>{t('admin.resetPasswordSuffix')}
          </p>
          <Input
            label={t('admin.newPassword')}
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder={t('login.placeholderPassword')}
            error={passwordError || undefined}
          />
          <Input
            label={t('admin.confirmPassword')}
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t('admin.repeatNewPassword')}
          />
        </div>
      </Modal>

      {/* Ban confirmation modal */}
      <Modal
        isOpen={!!banTarget}
        onClose={() => setBanTarget(null)}
        title={t('admin.banUserTitle')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setBanTarget(null)}>{t('admin.cancel')}</Button>
            <Button variant="danger" onClick={handleBan} disabled={banMutation.isPending}>
              {banMutation.isPending ? t('admin.banning') : t('admin.banAndDelete')}
            </Button>
          </>
        }
      >
        <div className={styles.banWarning}>
          <p>
            {t('admin.banConfirm')} <strong>{banTarget?.name}</strong>.
          </p>
          <ul className={styles.banList}>
            <li>{t('admin.banWarn1')}</li>
            <li>{t('admin.banWarn2')}</li>
            <li>{t('admin.banWarn3')}</li>
          </ul>
          <p className={styles.banNote}>{t('admin.banIrreversible')}</p>
        </div>
      </Modal>

      {/* User Inventory modal */}
      {inventoryTarget && (
        <UserInventoryModal
          userId={inventoryTarget.id}
          userName={inventoryTarget.name}
          onClose={() => setInventoryTarget(null)}
        />
      )}
    </div>
  );
}

/* ─── User Detail Modal (Inventory + Settings) ────────────────────────── */

function UserInventoryModal({ userId, userName, onClose }: { userId: string; userName: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'inventory' | 'settings'>('inventory');

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`${userName}`}
      maxWidth="70%"
      footer={<Button variant="secondary" onClick={onClose}>{t('admin.cancel')}</Button>}
    >
      <div className={styles.tabs} style={{ marginBottom: '1rem' }}>
        <button
          className={`${styles.tab} ${tab === 'inventory' ? styles.tabActive : ''}`}
          onClick={() => setTab('inventory')}
        >
          Inventory
        </button>
        <button
          className={`${styles.tab} ${tab === 'settings' ? styles.tabActive : ''}`}
          onClick={() => setTab('settings')}
        >
          Settings
        </button>
      </div>

      {tab === 'inventory' && <UserInventoryTab userId={userId} />}
      {tab === 'settings' && <UserSettingsTab userId={userId} userName={userName} />}
    </Modal>
  );
}

function UserInventoryTab({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const debouncedSearch = useDebounce(search, 300);

  const query: Record<string, string | number> = { page, limit: 20, sortBy, sortOrder };
  if (debouncedSearch) query.search = debouncedSearch;

  const { data, isLoading } = useUserInventory(userId, query);

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  }

  const items = data?.data ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <>
      <div className={styles.modalSearch}>
        <input
          className={styles.searchInput}
          type="text"
          placeholder={t('admin.searchItemsPlaceholder')}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
      </div>
      {isLoading ? (
        <div className={styles.loading}>{t('admin.loadingItems')}</div>
      ) : items.length === 0 ? (
        <div className={styles.loading}>{t('admin.noItemsFound')}</div>
      ) : (
        <ItemTable
          items={items}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          from="admin"
        />
      )}
      {totalPages > 1 && (
        <div className={styles.pagination} style={{ marginTop: '1rem' }}>
          <Button size="small" variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            {t('admin.prevPage')}
          </Button>
          <span className={styles.pageInfo}>
            {t('admin.pageOf').replace('{page}', String(page)).replace('{totalPages}', String(totalPages))}
          </span>
          <Button size="small" variant="secondary" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
            {t('admin.nextPage')}
          </Button>
        </div>
      )}
    </>
  );
}

function UserSettingsTab({ userId }: { userId: string; userName: string }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['admin', 'user-settings', userId],
    queryFn: () => apiClient.getUserSettings(userId),
  });

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'user'>('user');
  const [aiTier, setAiTier] = useState<'free' | 'pro'>('free');
  const [proUsageLimit, setProUsageLimit] = useState(500);
  // Profile
  const [bio, setBio] = useState('');
  const [location, setLocation] = useState('');
  const [profileSlug, setProfileSlug] = useState('');
  // AI Config
  const [aiProvider, setAiProvider] = useState('');
  const [aiApiKey, setAiApiKey] = useState('');
  const [aiEndpoint, setAiEndpoint] = useState('');
  const [aiDeployment, setAiDeployment] = useState('');
  const [aiApiVersion, setAiApiVersion] = useState('');
  const [aiModel, setAiModel] = useState('');

  const [syncedSettingsId, setSyncedSettingsId] = useState<string | null>(null);

  useEffect(() => {
    if (!settings) return;
    // Re-sync form when settings load or after save invalidation
    const settingsKey = `${userId}:${JSON.stringify(settings)}`;
    if (settingsKey === syncedSettingsId) return;
    setSyncedSettingsId(settingsKey);
    setDisplayName(settings.displayName);
    setEmail(settings.email);
    setRole(settings.role);
    setAiTier(settings.aiTier);
    setProUsageLimit(settings.proUsageLimit);
    setBio(settings.bio);
    setLocation(settings.location);
    setProfileSlug(settings.profileSlug ?? '');
    setAiProvider(settings.aiConfig?.provider ?? '');
    setAiEndpoint(settings.aiConfig?.endpoint ?? '');
    setAiDeployment(settings.aiConfig?.deployment ?? '');
    setAiApiVersion(settings.aiConfig?.apiVersion ?? '');
    setAiModel(settings.aiConfig?.model ?? '');
  }, [settings, userId]);

  const saveMutation = useMutation({
    mutationFn: (data: Parameters<typeof apiClient.updateUserSettings>[1]) =>
      apiClient.updateUserSettings(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-settings', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setSyncedSettingsId(null); // re-sync from server on next query resolution
      toast('Settings saved', 'success');
    },
    onError: () => toast('Failed to save settings', 'error'),
  });

  function handleSave() {
    const updates: Parameters<typeof apiClient.updateUserSettings>[1] = {};
    if (displayName !== settings?.displayName) updates.displayName = displayName;
    if (email !== settings?.email) updates.email = email;
    if (role !== settings?.role) updates.role = role;
    if (aiTier !== settings?.aiTier) updates.aiTier = aiTier;
    if (proUsageLimit !== settings?.proUsageLimit) updates.proUsageLimit = proUsageLimit;
    if (bio !== settings?.bio) updates.bio = bio;
    if (location !== settings?.location) updates.location = location;
    if (profileSlug !== (settings?.profileSlug ?? '')) updates.profileSlug = profileSlug;

    // AI config — only send if the key was entered (we never send the masked key back)
    if (aiApiKey && aiProvider) {
      updates.aiConfig = {
        provider: aiProvider,
        apiKey: aiApiKey,
        endpoint: aiEndpoint || undefined,
        deployment: aiDeployment || undefined,
        apiVersion: aiApiVersion || undefined,
        model: aiModel || undefined,
      };
    }

    if (Object.keys(updates).length === 0) {
      toast('No changes to save', 'info');
      return;
    }
    saveMutation.mutate(updates);
  }

  if (isLoading) {
    return <div className={styles.loading}>Loading settings...</div>;
  }

  if (!settings) {
    return <div className={styles.loading}>User not found</div>;
  }

  const needsEndpoint = aiProvider === 'azure' || aiProvider === 'custom';

  return (
    <div className={styles.settingsForm}>
      {/* Account */}
      <div className={styles.settingsGrid}>
        <div>
          <label className={styles.formLabel}>Display Name</label>
          <input className={styles.formInput} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </div>
        <div>
          <label className={styles.formLabel}>Email</label>
          <input className={styles.formInput} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
      </div>

      <div className={styles.settingsGrid3}>
        <div>
          <label className={styles.formLabel}>Role</label>
          <select className={styles.selectFull} value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'user')}>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div>
          <label className={styles.formLabel}>AI Tier</label>
          <select className={styles.selectFull} value={aiTier} onChange={(e) => setAiTier(e.target.value as 'free' | 'pro')}>
            <option value="free">Free</option>
            <option value="pro">Pro</option>
          </select>
        </div>
        <div>
          <label className={styles.formLabel}>Pro Usage Limit</label>
          <input className={styles.formInput} type="number" value={proUsageLimit} onChange={(e) => setProUsageLimit(Number(e.target.value))} min={0} />
        </div>
      </div>

      {settings.aiTier === 'pro' && (
        <div className={styles.settingsUsage}>
          Pro usage: {settings.proUsageCount} / {settings.proUsageLimit} analyses
          {settings.proUsagePeriodEnd && ` · Resets ${new Date(settings.proUsagePeriodEnd).toLocaleDateString()}`}
        </div>
      )}

      {/* Profile */}
      <div className={styles.settingsGrid3}>
        <div>
          <label className={styles.formLabel}>Profile Slug</label>
          <input className={styles.formInput} value={profileSlug} onChange={(e) => setProfileSlug(e.target.value)} placeholder="username" />
        </div>
        <div>
          <label className={styles.formLabel}>Location</label>
          <input className={styles.formInput} value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City, Country" />
        </div>
        <div>
          <label className={styles.formLabel}>Bio</label>
          <input className={styles.formInput} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Short bio" />
        </div>
      </div>

      {/* AI Configuration */}
      <div className={styles.settingsInfo}>
        <div className={styles.settingsInfoTitle}>
          AI Configuration
          {settings.aiConfig && (
            <span className={styles.settingsInfoBody}> — {settings.aiConfig.provider} (key: {settings.aiConfig.apiKeyMasked})</span>
          )}
        </div>
      </div>

      <div className={styles.settingsGrid}>
        <div>
          <label className={styles.formLabel}>Provider</label>
          <select className={styles.selectFull} value={aiProvider} onChange={(e) => setAiProvider(e.target.value)}>
            <option value="">None</option>
            <option value="openai">OpenAI</option>
            <option value="azure">Azure OpenAI</option>
            <option value="gemini">Gemini</option>
            <option value="custom">Custom</option>
          </select>
        </div>
        <div>
          <label className={styles.formLabel}>API Key</label>
          <input
            className={styles.formInput}
            type="password"
            value={aiApiKey}
            onChange={(e) => setAiApiKey(e.target.value)}
            placeholder={settings.aiConfig ? `Current: ${settings.aiConfig.apiKeyMasked}` : 'Enter API key'}
          />
        </div>
      </div>

      {needsEndpoint && (
        <div className={styles.settingsGrid}>
          <div>
            <label className={styles.formLabel}>Endpoint</label>
            <input className={styles.formInput} value={aiEndpoint} onChange={(e) => setAiEndpoint(e.target.value)} placeholder="https://..." />
          </div>
          {aiProvider === 'azure' && (
            <div>
              <label className={styles.formLabel}>Deployment</label>
              <input className={styles.formInput} value={aiDeployment} onChange={(e) => setAiDeployment(e.target.value)} placeholder="gpt-4o" />
            </div>
          )}
        </div>
      )}

      {aiProvider === 'azure' && (
        <div className={styles.settingsGrid}>
          <div>
            <label className={styles.formLabel}>API Version</label>
            <input className={styles.formInput} value={aiApiVersion} onChange={(e) => setAiApiVersion(e.target.value)} placeholder="2024-10-21" />
          </div>
          <div />
        </div>
      )}

      {aiProvider === 'gemini' && (
        <div className={styles.settingsGrid}>
          <div>
            <label className={styles.formLabel}>Model</label>
            <input className={styles.formInput} value={aiModel} onChange={(e) => setAiModel(e.target.value)} placeholder="gemini-2.5-flash" />
          </div>
          <div />
        </div>
      )}

      <div>
        <Button size="small" onClick={handleSave} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}

/* ─── Inventory Tab (Platform-wide) ─────────────────────────────────────── */

function InventoryPanel() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [platform, setPlatform] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const debouncedSearch = useDebounce(search, 300);

  const query: Record<string, any> = { page, limit: 20, sortBy, sortOrder, showAll: true };
  if (debouncedSearch) query.search = debouncedSearch;
  if (category) query.category = category;
  if (platform) query.platform = platform;

  const { data, isLoading } = useInventoryList(query);

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  }

  const items = data?.data ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div>
      <div className={styles.filterBar}>
        <input
          className={styles.searchInput}
          type="text"
          placeholder={t('admin.searchItemsPlaceholder')}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <select
          className={styles.roleSelect}
          value={category}
          onChange={(e) => { setCategory(e.target.value); setPage(1); }}
        >
          <option value="">{t('inventory.allCategories')}</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{t(`category.${c}` as any)}</option>
          ))}
        </select>
        <select
          className={styles.roleSelect}
          value={platform}
          onChange={(e) => { setPlatform(e.target.value); setPage(1); }}
        >
          <option value="">{t('inventory.allPlatforms')}</option>
          {PLATFORMS.map((p) => (
            <option key={p} value={p}>{t(`platform.${p}` as any)}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className={styles.loading}>{t('admin.loadingItems')}</div>
      ) : items.length === 0 ? (
        <div className={styles.loading}>{t('admin.noItemsFound')}</div>
      ) : (
        <ItemTable
          items={items}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          showOwner
          from="admin"
        />
      )}

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <Button size="small" variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            {t('admin.prevPage')}
          </Button>
          <span className={styles.pageInfo}>
            {t('admin.pageOf').replace('{page}', String(page)).replace('{totalPages}', String(totalPages))}
          </span>
          <Button size="small" variant="secondary" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
            {t('admin.nextPage')}
          </Button>
        </div>
      )}
    </div>
  );
}

/* ─── Licenses Tab ──────────────────────────────────────────────────────── */

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <button className={styles.copyBtn} onClick={handleCopy} title="Copy to clipboard">
      {copied ? <Check size={12} strokeWidth={2} /> : <Copy size={12} strokeWidth={2} />}
    </button>
  );
}

function LicensesPanel() {
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'revoked'>('all');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(search, 300);

  const query: Record<string, string | number> = { page, limit: 20, status };
  if (debouncedSearch) query.search = debouncedSearch;

  const { data, isLoading } = useAdminLicenses(query);
  const { data: stats } = useLicenseStats();
  const issueMutation = useIssueLicense();
  const revokeMutation = useRevokeLicense();
  const deleteMutation = useDeleteLicense();

  const [showIssueModal, setShowIssueModal] = useState(false);
  const [issueEmail, setIssueEmail] = useState('');
  const [issueNotes, setIssueNotes] = useState('');
  const [issuedKey, setIssuedKey] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<{ id: string; email: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; email: string } | null>(null);

  async function handleIssue(e: FormEvent) {
    e.preventDefault();
    try {
      const result = await issueMutation.mutateAsync({ email: issueEmail, notes: issueNotes || undefined });
      setIssuedKey(result.licenseKey);
      setIssueEmail('');
      setIssueNotes('');
    } catch {
      toast('Failed to issue license', 'error');
    }
  }

  function handleCloseIssueModal() {
    setShowIssueModal(false);
    setIssuedKey(null);
    setIssueEmail('');
    setIssueNotes('');
  }

  async function handleRevoke() {
    if (!revokeTarget) return;
    try {
      await revokeMutation.mutateAsync(revokeTarget.id);
      toast(`License for ${revokeTarget.email} revoked`, 'success');
      setRevokeTarget(null);
    } catch {
      toast('Failed to revoke license', 'error');
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast(`License for ${deleteTarget.email} deleted`, 'success');
      setDeleteTarget(null);
    } catch {
      toast('Failed to delete license', 'error');
    }
  }

  const licenses = data?.data ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div>
      {/* Stats row */}
      {stats && (
        <div className={styles.metricsGrid} style={{ marginBottom: '1.5rem' }}>
          <div className={styles.metricCard}>
            <KeyRound size={16} strokeWidth={1.5} className={styles.metricIcon} />
            <span className={styles.metricValue}>{stats.total}</span>
            <span className={styles.metricLabel}>Total Issued</span>
          </div>
          <div className={styles.metricCard}>
            <ShieldCheck size={16} strokeWidth={1.5} className={styles.metricIcon} />
            <span className={styles.metricValue}>{stats.active}</span>
            <span className={styles.metricLabel}>Active</span>
          </div>
          <div className={styles.metricCard}>
            <ShieldOff size={16} strokeWidth={1.5} className={styles.metricIcon} />
            <span className={styles.metricValue}>{stats.revoked}</span>
            <span className={styles.metricLabel}>Revoked</span>
          </div>
          <div className={styles.metricCard}>
            <Activity size={16} strokeWidth={1.5} className={styles.metricIcon} />
            <span className={styles.metricValue}>{stats.activations}</span>
            <span className={styles.metricLabel}>Total Activations</span>
          </div>
        </div>
      )}

      <div className={styles.sectionHeader}>
        <span className={styles.sectionCount}>{data?.total ?? 0} license{(data?.total ?? 0) !== 1 ? 's' : ''}</span>
        <Button size="small" onClick={() => setShowIssueModal(true)}>
          Issue License
        </Button>
      </div>

      <div className={styles.filterBar}>
        <input
          className={styles.searchInput}
          type="text"
          placeholder="Search by email…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <select
          className={styles.roleSelect}
          value={status}
          onChange={(e) => { setStatus(e.target.value as typeof status); setPage(1); }}
        >
          <option value="all">All</option>
          <option value="active">Active</option>
          <option value="revoked">Revoked</option>
        </select>
      </div>

      {isLoading ? (
        <div className={styles.loading}>Loading licenses…</div>
      ) : licenses.length === 0 ? (
        <div className={styles.loading}>No licenses found.</div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Email</th>
                <th>License Key</th>
                <th>Issued</th>
                <th>Issued By</th>
                <th>Activations</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {licenses.map((lic) => (
                <tr key={lic.id}>
                  <td className={styles.emailCell}>
                    {lic.email}
                    {lic.notes && (
                      <span className={styles.licenseNote} title={lic.notes}>
                        {lic.notes}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={styles.licenseKeyCell}>
                      <span className={styles.licenseKeyTruncated} title={lic.licenseKey}>
                        {lic.licenseKey.slice(0, 12)}…
                      </span>
                      <CopyButton text={lic.licenseKey} />
                    </span>
                  </td>
                  <td className={styles.dateCell}>{new Date(lic.issuedAt).toLocaleDateString()}</td>
                  <td className={styles.userName}>{lic.issuedByName ?? '—'}</td>
                  <td className={styles.countCell}>
                    {lic.activationCount}
                    {lic.lastActivatedAt && (
                      <span className={styles.lastActivated} title={`Last: ${new Date(lic.lastActivatedAt).toLocaleString()}`}>
                        {' '}({new Date(lic.lastActivatedAt).toLocaleDateString()})
                      </span>
                    )}
                  </td>
                  <td>
                    {lic.revokedAt ? (
                      <span className={styles.offerStatusBadge} data-status="declined" title={`Revoked by ${lic.revokedByName ?? '?'} on ${new Date(lic.revokedAt).toLocaleDateString()}`}>
                        Revoked
                      </span>
                    ) : (
                      <span className={styles.offerStatusBadge} data-status="accepted">Active</span>
                    )}
                  </td>
                  <td>
                    <div className={styles.rowActions}>
                      {!lic.revokedAt && (
                        <Button
                          size="small"
                          variant="danger"
                          onClick={() => setRevokeTarget({ id: lic.id, email: lic.email })}
                        >
                          Revoke
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="ghost"
                        onClick={() => setDeleteTarget({ id: lic.id, email: lic.email })}
                        title="Permanently delete this license record"
                      >
                        <Ban size={13} strokeWidth={2} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <Button size="small" variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            {/* t('admin.prevPage') */}Prev
          </Button>
          <span className={styles.pageInfo}>Page {page} of {totalPages}</span>
          <Button size="small" variant="secondary" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
            Next
          </Button>
        </div>
      )}

      {/* Issue License Modal */}
      <Modal
        isOpen={showIssueModal}
        onClose={handleCloseIssueModal}
        title="Issue License"
        disableOverlayClose
        footer={
          issuedKey ? (
            <Button onClick={handleCloseIssueModal}>Done</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={handleCloseIssueModal}>Cancel</Button>
              <Button onClick={handleIssue} disabled={issueMutation.isPending}>
                {issueMutation.isPending ? 'Issuing…' : 'Issue License'}
              </Button>
            </>
          )
        }
      >
        {issuedKey ? (
          <div className={styles.issuedKeyResult}>
            <p className={styles.issuedKeyNote}>
              License issued successfully. <strong>Copy this key now</strong> — it is shown only once.
            </p>
            <div className={styles.issuedKeyBox}>
              <code className={styles.issuedKeyText}>{issuedKey}</code>
              <CopyButton text={issuedKey} />
            </div>
          </div>
        ) : (
          <form className={styles.inviteForm} onSubmit={handleIssue}>
            <Input
              label="Email"
              type="email"
              value={issueEmail}
              onChange={(e) => setIssueEmail(e.target.value)}
              placeholder="user@example.com"
              required
            />
            <div>
              <label className={styles.formLabel}>Notes (optional)</label>
              <textarea
                className={styles.notesTextarea}
                value={issueNotes}
                onChange={(e) => setIssueNotes(e.target.value)}
                placeholder="Order ID, context, etc."
                rows={3}
              />
            </div>
          </form>
        )}
      </Modal>

      {/* Revoke Confirmation Modal */}
      <Modal
        isOpen={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        title="Revoke License"
        footer={
          <>
            <Button variant="secondary" onClick={() => setRevokeTarget(null)}>Cancel</Button>
            <Button variant="danger" onClick={handleRevoke} disabled={revokeMutation.isPending}>
              {revokeMutation.isPending ? 'Revoking…' : 'Revoke License'}
            </Button>
          </>
        }
      >
        <p className={styles.resetDesc}>
          Revoke the license for <strong>{revokeTarget?.email}</strong>? The desktop app will reject
          this key on its next online activation check.
        </p>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete License"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Deleting…' : 'Delete Permanently'}
            </Button>
          </>
        }
      >
        <p className={styles.resetDesc}>
          Permanently delete the license record for <strong>{deleteTarget?.email}</strong>?
          This removes all activation history and cannot be undone.
        </p>
      </Modal>
    </div>
  );
}

/* ─── Offers Tab ────────────────────────────────────────────────────────── */

function OffersPanel() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const debouncedSearch = useDebounce(search, 300);

  const query: Record<string, string | number> = { page, limit: 20, sortBy, sortOrder };
  if (status !== 'all') query.status = status;
  if (debouncedSearch) query.search = debouncedSearch;

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  }

  const { data, isLoading } = useAdminOffers(query);
  const forceWithdrawMutation = useForceWithdrawOffer();
  const [withdrawTarget, setWithdrawTarget] = useState<string | null>(null);

  async function handleForceWithdraw() {
    if (!withdrawTarget) return;
    try {
      await forceWithdrawMutation.mutateAsync(withdrawTarget);
      toast(t('admin.forceWithdrawSuccess'), 'success');
      setWithdrawTarget(null);
    } catch {
      toast(t('admin.forceWithdrawFailed'), 'error');
    }
  }

  const offers = data?.data ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div>
      <div className={styles.filterBar}>
        <input
          className={styles.searchInput}
          type="text"
          placeholder={t('admin.searchOffersPlaceholder')}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <select
          className={styles.roleSelect}
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
        >
          <option value="all">{t('admin.allStatuses')}</option>
          <option value="pending">{t('admin.pending')}</option>
          <option value="accepted">{t('admin.accepted')}</option>
          <option value="declined">{t('admin.declined')}</option>
          <option value="withdrawn">{t('admin.withdrawn')}</option>
        </select>
      </div>

      {isLoading ? (
        <div className={styles.loading}>{t('admin.loadingOffers')}</div>
      ) : offers.length === 0 ? (
        <div className={styles.loading}>{t('admin.noOffers')}</div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('admin.offerFrom')}</th>
                <th>{t('admin.offerTo')}</th>
                <th
                  className={styles.sortableHeader}
                  onClick={() => handleSort('status')}
                >
                  {t('admin.offerStatus')}
                  {sortBy === 'status' && (
                    <span className={styles.sortIndicator}>
                      {sortOrder === 'asc' ? '\u25B2' : '\u25BC'}
                    </span>
                  )}
                </th>
                <th>{t('admin.offerItems')}</th>
                <th
                  className={styles.sortableHeader}
                  onClick={() => handleSort('moneyAmount')}
                >
                  {t('admin.offerMoney')}
                  {sortBy === 'moneyAmount' && (
                    <span className={styles.sortIndicator}>
                      {sortOrder === 'asc' ? '\u25B2' : '\u25BC'}
                    </span>
                  )}
                </th>
                <th
                  className={styles.sortableHeader}
                  onClick={() => handleSort('createdAt')}
                >
                  {t('admin.offerCreated')}
                  {sortBy === 'createdAt' && (
                    <span className={styles.sortIndicator}>
                      {sortOrder === 'asc' ? '\u25B2' : '\u25BC'}
                    </span>
                  )}
                </th>
                <th>{t('admin.offerActions')}</th>
              </tr>
            </thead>
            <tbody>
              {offers.map((o) => (
                <tr key={o.id}>
                  <td className={styles.userName}>{o.fromName}</td>
                  <td className={styles.userName}>{o.toName}</td>
                  <td>
                    <span className={styles.offerStatusBadge} data-status={o.status}>
                      {t(`admin.${o.status}` as any)}
                    </span>
                  </td>
                  <td className={styles.countCell}>
                    {o.offeredCount + o.requestedCount}
                  </td>
                  <td className={styles.valueCell}>
                    {o.moneyAmount ? `${o.moneyCurrency} ${Number(o.moneyAmount).toLocaleString()}` : '—'}
                  </td>
                  <td className={styles.dateCell}>
                    {new Date(o.createdAt).toLocaleDateString()}
                  </td>
                  <td>
                    {o.status === 'pending' && (
                      <Button
                        size="small"
                        variant="danger"
                        onClick={() => setWithdrawTarget(o.id)}
                      >
                        {t('admin.forceWithdraw')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <Button size="small" variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            {t('admin.prevPage')}
          </Button>
          <span className={styles.pageInfo}>
            {t('admin.pageOf').replace('{page}', String(page)).replace('{totalPages}', String(totalPages))}
          </span>
          <Button size="small" variant="secondary" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
            {t('admin.nextPage')}
          </Button>
        </div>
      )}

      {/* Force Withdraw confirmation modal */}
      <Modal
        isOpen={!!withdrawTarget}
        onClose={() => setWithdrawTarget(null)}
        title={t('admin.forceWithdrawTitle')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setWithdrawTarget(null)}>{t('admin.cancel')}</Button>
            <Button variant="danger" onClick={handleForceWithdraw} disabled={forceWithdrawMutation.isPending}>
              {forceWithdrawMutation.isPending ? t('admin.forceWithdrawing') : t('admin.forceWithdraw')}
            </Button>
          </>
        }
      >
        <p className={styles.resetDesc}>{t('admin.forceWithdrawConfirm')}</p>
      </Modal>
    </div>
  );
}

/* ─── Feedback Panel ──────────────────────────────────────────────────── */

function getFeedbackTypeIcon(type: string) {
  switch (type) {
    case 'bug': return Bug;
    case 'feature': return Lightbulb;
    case 'feedback': return MessageCircle;
    case 'question': return HelpCircle;
    default: return MessageCircle;
  }
}

function FeedbackPanel() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState<Record<string, string>>({});

  const query: Record<string, string> = {};
  if (statusFilter) query.status = statusFilter;
  if (typeFilter) query.type = typeFilter;

  const { data: statsData } = useAdminFeedbackStats();
  const { data: feedbackData, isLoading } = useAdminFeedbackList(query);
  const updateStatus = useAdminUpdateFeedbackStatus();
  const replyMutation = useAdminReplyFeedback();

  const stats = statsData as Record<string, number> | undefined;
  const tickets = feedbackData?.data ?? [];

  const statCards: { label: string; value: number; cls: string }[] = [
    { label: t('feedback.statusOpen'), value: stats?.open ?? 0, cls: styles.heroCard },
    { label: t('feedback.statusInProgress'), value: stats?.in_progress ?? 0, cls: styles.heroCard },
    { label: t('feedback.statusResolved'), value: stats?.resolved ?? 0, cls: styles.heroCard },
    { label: t('feedback.statusClosed'), value: stats?.closed ?? 0, cls: styles.heroCard },
  ];

  function handleStatusChange(id: string, status: string) {
    updateStatus.mutate({ id, status });
  }

  function handleReply(id: string) {
    const text = replyText[id]?.trim();
    if (!text) return;
    replyMutation.mutate({ id, reply: text }, {
      onSuccess: () => {
        setReplyText((prev) => ({ ...prev, [id]: '' }));
        toast('Reply sent', 'success');
      },
    });
  }

  return (
    <div>
      <h2 className={styles.sectionHeader}>{t('admin.feedback.title')}</h2>

      {/* Stats row */}
      <div className={styles.heroGrid}>
        {statCards.map((card) => (
          <div key={card.label} className={card.cls}>
            <div className={styles.heroContent}>
              <span className={styles.heroValue}>{card.value}</span>
              <span className={styles.heroLabel}>{card.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <select
          className={styles.filterSelect}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">{t('admin.feedback.changeStatus')} — All</option>
          <option value="open">{t('feedback.statusOpen')}</option>
          <option value="in_progress">{t('feedback.statusInProgress')}</option>
          <option value="resolved">{t('feedback.statusResolved')}</option>
          <option value="closed">{t('feedback.statusClosed')}</option>
        </select>
        <select
          className={styles.filterSelect}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="">Type — All</option>
          <option value="bug">{t('feedback.typeBug')}</option>
          <option value="feature">{t('feedback.typeFeature')}</option>
          <option value="feedback">{t('feedback.typeFeedback')}</option>
          <option value="question">{t('feedback.typeQuestion')}</option>
        </select>
      </div>

      {/* Ticket list */}
      {isLoading ? (
        <div className={styles.loading}>{t('admin.loadingStats')}</div>
      ) : tickets.length === 0 ? (
        <div className={styles.loading}>{t('admin.feedback.noTickets')}</div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th>{t('feedback.titleLabel')}</th>
                <th>User</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket: any) => {
                const TypeIcon = getFeedbackTypeIcon(ticket.type);
                const expanded = expandedId === ticket.id;
                return (
                  <>
                    <tr
                      key={ticket.id}
                      onClick={() => setExpandedId(expanded ? null : ticket.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td><TypeIcon size={14} strokeWidth={1.75} /></td>
                      <td>{ticket.title}</td>
                      <td className={styles.dateCell}>{ticket.user?.displayName ?? ticket.userId?.slice(0, 8)}</td>
                      <td>
                        <select
                          className={styles.filterSelect}
                          value={ticket.status}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => handleStatusChange(ticket.id, e.target.value)}
                          style={{ minWidth: 110 }}
                        >
                          <option value="open">{t('feedback.statusOpen')}</option>
                          <option value="in_progress">{t('feedback.statusInProgress')}</option>
                          <option value="resolved">{t('feedback.statusResolved')}</option>
                          <option value="closed">{t('feedback.statusClosed')}</option>
                        </select>
                      </td>
                      <td className={styles.dateCell}>{new Date(ticket.createdAt).toLocaleDateString()}</td>
                    </tr>
                    {expanded && (
                      <tr key={`${ticket.id}-detail`}>
                        <td colSpan={5} style={{ padding: '0.75rem 1rem', background: 'var(--bg-tertiary, var(--bg-secondary))' }}>
                          {ticket.description && (
                            <p style={{ margin: '0 0 0.75rem', fontSize: '0.8125rem', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
                              {ticket.description}
                            </p>
                          )}
                          {ticket.adminReply && (
                            <div style={{ padding: '0.5rem 0.75rem', marginBottom: '0.75rem', borderInlineStart: '3px solid var(--accent)', background: 'var(--bg-primary)', borderRadius: 'var(--radius-sm)' }}>
                              <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--accent)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                {t('feedback.adminReply')}
                              </div>
                              <div style={{ fontSize: '0.8125rem', color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{ticket.adminReply}</div>
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
                            <textarea
                              value={replyText[ticket.id] ?? ''}
                              onChange={(e) => setReplyText((prev) => ({ ...prev, [ticket.id]: e.target.value }))}
                              placeholder={t('admin.feedback.replyPlaceholder')}
                              rows={2}
                              style={{
                                flex: 1, padding: '0.5rem 0.75rem', border: '1px solid var(--border)',
                                borderRadius: 'var(--radius-sm)', background: 'var(--bg-primary)',
                                color: 'var(--text-primary)', fontFamily: 'var(--font-family)',
                                fontSize: '0.8125rem', resize: 'vertical',
                              }}
                            />
                            <Button
                              size="small"
                              onClick={() => handleReply(ticket.id)}
                              disabled={!replyText[ticket.id]?.trim() || replyMutation.isPending}
                            >
                              <Send size={12} strokeWidth={2} />
                              {t('admin.feedback.sendReply')}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
